import { AppDispatch } from "../../../store/store";
import { setPaymentPreferenceApi } from "../../../apis/apis";
import { refreshAccountSummary } from "../../../AccountSummary.slice";
import { refreshPaymentMethodsPage } from "../../PaymentMethods/refreshPaymentMethodsPage";
import { pushToast } from "../../Toaster/Toaster.slice";
import { AccountSummaryData } from "../../../AccountSummary.slice";

export const MODAL_ACTION_CANCELLED = "MODAL_ACTION_CANCELLED";

export const isModalActionCancelled = (error: unknown): boolean =>
  error === MODAL_ACTION_CANCELLED;

export const formatAutopayApiError = (e: any): string => {
  if (isModalActionCancelled(e)) {
    return "";
  }

  if (e?.response?.data?.message) {
    return `${e.response.data.message}: ${e.message ?? "Request failed"}`;
  }

  return `Error: ${e?.message ?? "An unexpected error occurred"}`;
};

type AutopayBatchOptions = {
  showToast?: boolean;
  refresh?: boolean;
  toastMessage?: string;
};

type ReassignAutopayOptions = AutopayBatchOptions;

export const isDelegateAutopayAccount = (account: AccountSummaryData): boolean =>
  Boolean(account.autopay?.enabled) && account.autopay?.owner === false;

export const isOwnerEnrolledInAutopay = (
  account: AccountSummaryData
): boolean =>
  Boolean(account.autopay?.enabled) && !isDelegateAutopayAccount(account);

export const canEnableAutopayForAccount = (
  account: AccountSummaryData
): boolean => !account.autopay?.enabled;

export const getPaymentMethodDisplay = (
  paymentMethods: { id: string; display: string }[],
  paymentMethodId?: string | null
): string | undefined =>
  paymentMethods.find((paymentMethod) => paymentMethod.id === paymentMethodId)
    ?.display;

export type AutopayEnrollmentAssignment = {
  tenantAccountId: string;
  paymentMethodId: string;
};

export const reassignAutopayPaymentMethodForAccounts = async (
  dispatch: AppDispatch,
  tenantAccountIds: string[],
  paymentMethodId: string,
  options: ReassignAutopayOptions = {}
): Promise<void> => {
  const { showToast = true, refresh = true, toastMessage } = options;

  if (tenantAccountIds.length === 0) {
    return;
  }

  await Promise.all(
    tenantAccountIds.map((tenantAccountId) =>
      setPaymentPreferenceApi(tenantAccountId, "autopay", true, paymentMethodId)
    )
  );

  if (refresh) {
    await refreshPaymentMethodsPage(dispatch);
  }

  if (showToast) {
    dispatch(
      pushToast({
        message:
          toastMessage ??
          "AutoPay payment source successfully updated",
        type: "success",
      })
    );
  }
};

export const enableAutopayForAccounts = async (
  dispatch: AppDispatch,
  tenantAccountIds: string[],
  paymentMethodId: string,
  options: Omit<ReassignAutopayOptions, "toastMessage"> & {
    toastMessage?: string;
  } = {}
): Promise<void> => {
  const toastMessage =
    options.toastMessage ??
    (tenantAccountIds.length > 1
      ? `AutoPay successfully enabled for ${tenantAccountIds.length} service accounts`
      : "AutoPay successfully enabled");

  await reassignAutopayPaymentMethodForAccounts(
    dispatch,
    tenantAccountIds,
    paymentMethodId,
    {
      ...options,
      toastMessage,
    }
  );
};

export const enableAutopayWithAssignments = async (
  dispatch: AppDispatch,
  enrollments: AutopayEnrollmentAssignment[],
  options: Omit<ReassignAutopayOptions, "toastMessage"> & {
    toastMessage?: string;
  } = {}
): Promise<void> => {
  if (enrollments.length === 0) {
    return;
  }

  const { showToast = true, refresh = true, toastMessage } = options;

  await Promise.all(
    enrollments.map(({ tenantAccountId, paymentMethodId }) =>
      setPaymentPreferenceApi(tenantAccountId, "autopay", true, paymentMethodId)
    )
  );

  if (refresh) {
    await refreshPaymentMethodsPage(dispatch);
  }

  if (showToast) {
    dispatch(
      pushToast({
        message:
          toastMessage ??
          (enrollments.length > 1
            ? `AutoPay successfully enabled for ${enrollments.length} service accounts`
            : "AutoPay successfully enabled"),
        type: "success",
      })
    );
  }
};

export const setAutopayPaymentMethodForAccount = async (
  dispatch: AppDispatch,
  tenantAccountId: string,
  paymentMethodId: string
): Promise<void> => {
  await reassignAutopayPaymentMethodForAccounts(
    dispatch,
    [tenantAccountId],
    paymentMethodId
  );
};

export const disableAutopayForAccounts = async (
  dispatch: AppDispatch,
  tenantAccountIds: string[],
  options: AutopayBatchOptions = {}
): Promise<void> => {
  const { showToast = true, refresh = true, toastMessage } = options;

  if (tenantAccountIds.length === 0) {
    return;
  }

  await Promise.all(
    tenantAccountIds.map((tenantAccountId) =>
      setPaymentPreferenceApi(tenantAccountId, "autopay", false)
    )
  );

  if (refresh) {
    await dispatch(refreshAccountSummary());
  }

  if (showToast) {
    dispatch(
      pushToast({
        message:
          toastMessage ??
          (tenantAccountIds.length > 1
            ? `AutoPay disabled for ${tenantAccountIds.length} service accounts`
            : "AutoPay successfully disabled"),
        type: "success",
      })
    );
  }
};
